<script setup lang="ts">
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { getEquipName } from '@/utils/equip'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { socketEmitAsync } from '@/utils/web-socket'
import type { ItemType } from '@/types/item'

const userStore = useUserStore()

const itemList = computed(() => {
  return userStore.getItemEquipInfoList.filter((item) => {
    if (item.isLock || item.reinforcedLevel || item.starAttrs?.length || item.enchantAttr) return false
    if (!['peerless'].includes(item.quality)) return false
    if (extractIdList.value.includes(item.id)) return false
    if (extractItemList.value.length && extractItemList.value[0].quality !== item.quality) return false
    return true
  })
})
const extractIdList = ref<string[]>([])
const extractItemList = computed(() => {
  return userStore.getItemEquipInfoList.filter((item) => {
    return extractIdList.value.includes(item.id)
  })
})

const equipInfo = ref<EquipItemType>()
const isShowEquipInfo = ref(false)
async function handleAction(equipItem: EquipItemType, type: 'putIn' | 'takeOut') {
  if (isShowEquipInfo.value) return (equipInfo.value = equipItem)
  const { id } = equipItem
  if (!userStore.warehouseItemList) userStore.warehouseItemList = []
  if (type === 'putIn') {
    if ((extractItemList.value[0]?.quality === 'antiquity' && extractIdList.value.length >= 3) || extractIdList.value.length >= 20) {
      return ElMessage.warning('放入装备材料已达要求')
    }
    extractIdList.value.push(id)
  } else {
    const index = extractIdList.value.findIndex((item) => item === id)
    if (index === -1) return
    extractIdList.value.splice(index, 1)
  }
}

function handleClear() {
  extractIdList.value = []
}
function handleExtract() {
  const crackStone = extractIdList.value.length * 5
  ElMessageBox.confirm(
    `<p class="affix">您确定要进行<span class="info">萃取</span>吗？该操作将导致您材料台当中的装备<span class="hp">全部销毁</span>，请务必谨慎（本次萃取可获得${crackStone} 🔷裂隙晶石）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<ItemType>({
        type: 'extractEquip',
        data: { idList: extractIdList.value },
      })
      for (const id of extractIdList.value) {
        const index = userStore.itemList.findIndex((item) => item.id === id)
        userStore.itemList.splice(index, 1)
      }

      extractIdList.value = []
      userStore.consumables.crackStone += crackStone

      ElMessage.success(`萃取成功，获得${crackStone} 🔷裂隙晶石`)
    })
    .catch(() => {})
}
</script>

<template>
  <div class="extract">
    <p class="header">
      <el-popover placement="bottom" width="100%" trigger="click">
        <template #reference>
          <el-icon size="18"><QuestionFilled /></el-icon>
        </template>
        <div class="affix">
          <h4>萃取说明</h4>
          <p>1. 将<span class="peerless">绝世</span>装备用于萃取，可获得<span class="peerless">裂隙晶石</span>。</p>
          <p>2. 您需要将装备放入<span class="info">材料台</span>内，而后点击<span class="info">萃取</span>。</p>
          <p>3. 您无法将<span class="info">已锁定、已强化、已升星、已附魔</span>的装备放入材料台。</p>
        </div>
      </el-popover>
      <span>🔷 {{ userStore.consumables.crackStone }}</span>
    </p>
    <div class="main">
      <div class="item-list">
        <div class="border-wrap">
          <h4>背包</h4>
          <div style="display: flex; align-items: center; padding: 0 5px">
            <span style="font-size: 12px">查看装备信息：</span>
            <el-switch v-model="isShowEquipInfo" size="small" @change="equipInfo = undefined" />
          </div>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of itemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'putIn')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </el-col>
            </el-row>
            <p class="affix" style="text-align: center" v-show="!itemList.length">您的背包中不存在<span class="peerless">绝世</span>装备</p>
          </div>
        </div>
        <div class="border-wrap" style="padding: 10px; min-height: 50%; margin-top: 5px" v-show="isShowEquipInfo">
          <h4>装备信息</h4>
          <template v-if="equipInfo">
            <EquipInfo :equip-info="equipInfo" :show-action="false" :show-lock="false" />
          </template>
        </div>
      </div>
      <div class="item-list">
        <div class="action border-wrap">
          <h4>操作</h4>
          <el-button type="primary" :disabled="!Boolean(extractIdList.length)" plain size="small" @click="handleExtract">萃取</el-button>
          <el-button type="warning" plain size="small" @click="handleClear">清空材料台</el-button>
        </div>
        <div class="border-wrap">
          <h4>材料台</h4>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of extractItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'takeOut')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"> </span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.extract {
  padding-top: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .header {
    display: flex;
    justify-content: space-between;
    margin: 0 0 5px 0;
    padding: 0 15px;
    font-size: 14px;
  }
  .item-filter {
    margin-bottom: 10px;
    display: flex;
    .el-button {
      margin-left: 10px;
    }
  }
  .main {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    flex: 1;
  }
  .item-list {
    width: calc(50% - 5px);
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .border-wrap {
      overflow: hidden;
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
      &.action {
        height: auto;
        margin-bottom: 10px;
        padding: 0 10px 10px 10px;
        flex-shrink: 0;
        .el-button {
          margin-bottom: 10px;
          margin-left: 0;
        }
      }
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
